import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { format } from 'date-fns';
import { CheckIcon, MailIcon, RotateCcwIcon, SearchIcon, SendIcon, ShieldCheckIcon, TicketIcon, XIcon } from 'lucide-react';
import { useRegistrations } from '../context/RegistrationsContext';
import { events, getEventById } from '../data/events';
import { EmailPreview } from '../components/EmailPreview';
import { ETicket } from '../components/ETicket';
import { Registration, RegistrationStatus } from '../types';
const statusStyles: Record<RegistrationStatus, string> = {
  pending: 'bg-paper-warm text-ink-soft border-paper-line',
  approved: 'bg-signal/10 text-signal-deep border-signal/25',
  declined: 'bg-ink/5 text-ink-muted border-ink/15'
};
const tabs: Array<{
  key: RegistrationStatus | 'all';
  label: string;
}> = [{
  key: 'pending',
  label: 'Pending review'
}, {
  key: 'approved',
  label: 'Approved'
}, {
  key: 'declined',
  label: 'Declined'
}, {
  key: 'all',
  label: 'All'
}];
export function AdminDesk() {
  const {
    registrations,
    outbox,
    decide,
    decideMany,
    resendTicket,
    reset
  } = useRegistrations();
  const [tab, setTab] = useState<RegistrationStatus | 'all'>('pending');
  const [eventFilter, setEventFilter] = useState('all');
  const [query, setQuery] = useState('');
  const [selected, setSelected] = useState<string[]>([]);
  const [inspecting, setInspecting] = useState<Registration | null>(null);
  const [showOutbox, setShowOutbox] = useState(true);
  const filtered = useMemo(() => {
    const search = query.trim().toLowerCase();
    return registrations.filter(registration => tab === 'all' ? true : registration.status === tab).filter(registration => eventFilter === 'all' ? true : registration.eventId === eventFilter).filter(registration => search ? [registration.fullName, registration.email, registration.organisation, registration.ticketCode].join(' ').toLowerCase().includes(search) : true).sort((a, b) => new Date(b.submittedAt).getTime() - new Date(a.submittedAt).getTime());
  }, [registrations, tab, eventFilter, query]);
  const counts = useMemo(() => ({
    pending: registrations.filter(r => r.status === 'pending').length,
    approved: registrations.filter(r => r.status === 'approved').length,
    declined: registrations.filter(r => r.status === 'declined').length,
    all: registrations.length
  }), [registrations]);
  function toggle(id: string) {
    setSelected(prev => prev.includes(id) ? prev.filter(item => item !== id) : [...prev, id]);
  }
  const selectableIds = filtered.map(registration => registration.id);
  const allSelected = selectableIds.length > 0 && selectableIds.every(id => selected.includes(id));
  return <main className="w-full bg-paper" data-id="element-215" data-mp="3ud7kha2ylcsj">
      <section className="border-b border-paper-line bg-ink" data-id="element-216" data-mp="3plg2vp1s6hej">
        <div className="mx-auto max-w-content px-5 py-14 sm:px-8" data-id="element-217" data-mp="3pjv6dtvwooe1">
          <div className="flex flex-wrap items-center gap-3" data-id="element-218" data-mp="ryl7ey2350x">
            <span className="inline-flex items-center gap-2 rounded-full bg-signal px-3.5 py-1.5 text-[11px] font-medium uppercase tracking-[0.14em] text-white" data-id="element-219" data-mp="5prqm5rjbv05">
              <ShieldCheckIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-220" data-mp="ant9oqoynubm" />
              Human Planet admin
            </span>
            <span className="text-xs text-white/50" data-id="element-221" data-mp="jpsrvmbpp4g5">Signed in as events@human-planet.com</span>
          </div>
          <h1 className="mt-6 font-display text-3xl font-bold text-white sm:text-4xl" data-id="element-222" data-mp="oom7bkak75iy">Approve tickets</h1>
          <p className="mt-4 max-w-2xl text-sm leading-relaxed text-white/60" data-id="element-223" data-mp="tf2j6zyqej37">
            Every registration arrives pending. Approving emails the attendee their ticket as a PDF attachment.
            Declining sends a short courteous note. Nothing leaves this desk without a decision.
          </p>
          <dl className="mt-10 grid grid-cols-2 gap-6 border-t border-white/15 pt-8 sm:grid-cols-4" data-id="element-224" data-mp="17eu5rp3bd7st">
            {[{
            label: 'Pending review',
            value: counts.pending
          }, {
            label: 'Approved',
            value: counts.approved
          }, {
            label: 'Declined',
            value: counts.declined
          }, {
            label: 'Emails sent',
            value: outbox.length
          }].map(stat => <div key={stat.label} data-id="element-225" data-mp="yiv0bdl10yvw">
                <dd className="font-display text-3xl font-semibold text-white" data-id="element-226" data-mp="yjwo8in55fv1">{stat.value}</dd>
                <dt className="eyebrow mt-1.5 text-white/50" data-id="element-227" data-mp="auap1v6rr0n1">{stat.label}</dt>
              </div>)}
          </dl>
          <Link to="/" className="mt-8 inline-block text-xs text-white/50 transition-colors hover:text-white" data-id="element-228" data-mp="6ua1nl1ozf3u">
            Back to the public site
          </Link>
        </div>
      </section>

      <div className="mx-auto max-w-content px-5 py-12 sm:px-8" data-id="element-229" data-mp="fuye4bbft7em">
        <div className="rounded-xl border border-signal/25 bg-signal/5 px-5 py-4 text-sm text-ink-soft" data-id="element-230" data-mp="1w62exump82bu">
          <p data-id="element-231" data-mp="1r78zhwnuq191">
            <span className="font-medium text-ink" data-id="element-232" data-mp="1r7sh1c9lanmj">Test registration ready.</span> jonathan@mzninternational.com is in
            the pending queue for the GIIN Impact Forum. Approve it to send the confirmation email with the PDF ticket
            attached, then open the outbox below to read exactly what lands in the inbox.
          </p>
        </div>

        <div className="mt-8 flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between" data-id="element-233" data-mp="353k2jurla1z6">
          <div className="flex flex-wrap gap-2" role="tablist" aria-label="Filter by status" data-id="element-234" data-mp="3st4ngj7az9b3">
            {tabs.map(item => <button key={item.key} type="button" role="tab" aria-selected={tab === item.key} onClick={() => {
            setTab(item.key);
            setSelected([]);
          }} className={['rounded-full border px-4 py-2 text-sm transition-colors', tab === item.key ? 'border-signal bg-signal text-white' : 'border-paper-line bg-white text-ink-muted hover:border-signal/40 hover:text-ink'].join(' ')} data-id="element-235" data-mp="okb4cmfsbcl">
                {item.label}
                <span className="ml-2 font-mono text-xs opacity-70" data-id="element-236" data-mp="kbdri5lidl9q">{counts[item.key]}</span>
              </button>)}
          </div>

          <div className="flex flex-wrap items-center gap-3" data-id="element-237" data-mp="fc115avfhw2e">
            <label className="relative" data-id="element-238" data-mp="bbh385316of7">
              <span className="sr-only" data-id="element-239" data-mp="bca4wrn4unqh">Search registrations</span>
              <SearchIcon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-ink-muted" aria-hidden="true" data-id="element-240" data-mp="6jz7pwjs1g1m" />
              <input value={query} onChange={e => setQuery(e.target.value)} placeholder="Name, organisation or ticket ref" className="w-full rounded-lg border border-paper-line bg-white py-2.5 pl-9 pr-3 text-sm text-ink placeholder:text-ink-muted/60 focus:border-signal focus:outline-none focus:ring-2 focus:ring-signal/20 sm:w-72" data-id="element-241" data-mp="3sryzbj2em8b9" />
            </label>
            <label data-id="element-242" data-mp="238qpephu2fwg">
              <span className="sr-only" data-id="element-243" data-mp="23cejpqv3r6c8">Filter by event</span>
              <select value={eventFilter} onChange={e => setEventFilter(e.target.value)} className="rounded-lg border border-paper-line bg-white px-3 py-2.5 text-sm text-ink focus:border-signal focus:outline-none focus:ring-2 focus:ring-signal/20" data-id="element-244" data-mp="28ag0u68ywvl2">
                <option value="all" data-id="element-245" data-mp="1kkdd22c36bs9">All events</option>
                {events.map(event => <option key={event.id} value={event.id} data-id="element-246" data-mp="1phc8co7hkos1">
                    {event.title}
                  </option>)}
              </select>
            </label>
            <button type="button" onClick={() => setShowOutbox(prev => !prev)} className="inline-flex items-center gap-2 rounded-lg border border-paper-line bg-white px-4 py-2.5 text-sm text-ink transition-colors hover:border-signal/40" aria-pressed={showOutbox} data-id="element-247" data-mp="wt80px5cu1mp">
              <MailIcon className="h-4 w-4 text-signal" aria-hidden="true" data-id="element-248" data-mp="3mee7e8zex3mg" />
              Outbox
              <span className="font-mono text-xs text-ink-muted" data-id="element-249" data-mp="47zsa6peqnmh">{outbox.length}</span>
            </button>
          </div>
        </div>

        {selected.length > 0 && <div className="mt-6 flex flex-wrap items-center gap-3 rounded-xl border border-signal/25 bg-signal/5 px-4 py-3" data-id="element-250" data-mp="e3l22r0pws6s">
            <p className="text-sm text-ink" data-id="element-251" data-mp="95jb7jx1vokr">
              {selected.length} selected. Sending a decision emails all of them immediately.
            </p>
            <div className="ml-auto flex gap-2" data-id="element-252" data-mp="ivci47m8zfio">
              <button type="button" onClick={() => {
            decideMany(selected, 'approved');
            setSelected([]);
          }} className="inline-flex items-center gap-1.5 rounded-full bg-signal px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-signal-deep" data-id="element-253" data-mp="ixp9fbdwb28e">
                <CheckIcon className="h-4 w-4" aria-hidden="true" data-id="element-254" data-mp="1gfumhhaoocp3" />
                Approve and send tickets
              </button>
              <button type="button" onClick={() => {
            decideMany(selected, 'declined');
            setSelected([]);
          }} className="inline-flex items-center gap-1.5 rounded-full border border-ink/25 px-4 py-2 text-sm font-medium text-ink-soft transition-colors hover:bg-ink/5" data-id="element-255" data-mp="11lzgcj1wx1hl">
                <XIcon className="h-4 w-4" aria-hidden="true" data-id="element-256" data-mp="iuykw5003o2s" />
                Decline
              </button>
            </div>
          </div>}

        <div className="mt-6 overflow-hidden rounded-xl border border-paper-line bg-white" data-id="element-257" data-mp="9syyex67vrsg">
          <table className="w-full text-left text-sm" data-id="element-258" data-mp="du2ijvvas9zh">
            <caption className="sr-only" data-id="element-259" data-mp="dtiya8i6d9ia">Registrations awaiting a decision</caption>
            <thead className="border-b border-paper-line bg-paper-warm text-xs uppercase tracking-[0.1em] text-ink-muted" data-id="element-260" data-mp="1g6azlv1xpues">
              <tr data-id="element-261" data-mp="1l42p6if3x4ft">
                <th scope="col" className="w-10 px-4 py-3" data-id="element-262" data-mp="176e4bal1hxar">
                  <input type="checkbox" className="h-4 w-4 rounded border-paper-line text-signal focus:ring-signal/30" checked={allSelected} onChange={() => setSelected(allSelected ? [] : selectableIds)} aria-label="Select all visible registrations" data-id="element-263" data-mp="177yygz3fze83" />
                </th>
                <th scope="col" className="px-4 py-3 font-medium" data-id="element-264" data-mp="1q4h0c9c08ui2">
                  Attendee
                </th>
                <th scope="col" className="hidden px-4 py-3 font-medium lg:table-cell" data-id="element-265" data-mp="1u50vx8gxjq23">
                  Event
                </th>
                <th scope="col" className="hidden px-4 py-3 font-medium sm:table-cell" data-id="element-266" data-mp="2mn4u0srf5qia">
                  Registered
                </th>
                <th scope="col" className="px-4 py-3 font-medium" data-id="element-267" data-mp="b74bsvnx9kvj">
                  Status
                </th>
                <th scope="col" className="px-4 py-3 text-right font-medium" data-id="element-268" data-mp="68b4jkz8aq3t">
                  Decision
                </th>
              </tr>
            </thead>
            <tbody data-id="element-269" data-mp="fycktqlcb486">
              {filtered.map(registration => {
              const event = getEventById(registration.eventId);
              return <tr key={registration.id} className="border-b border-paper-line align-top last:border-0" data-id="element-270" data-mp="3ogsa28eat191">
                    <td className="px-4 py-4" data-id="element-271" data-mp="3jhz5x6kwimlj">
                      <input type="checkbox" className="h-4 w-4 rounded border-paper-line text-signal focus:ring-signal/30" checked={selected.includes(registration.id)} onChange={() => toggle(registration.id)} aria-label={`Select ${registration.fullName}`} data-id="element-272" data-mp="28itn1md47va" />
                    </td>
                    <td className="px-4 py-4" data-id="element-273" data-mp="1fqhyme5muj3s">
                      <button type="button" onClick={() => setInspecting(registration)} className="text-left font-medium text-ink underline decoration-transparent underline-offset-4 transition hover:decoration-signal" data-id="element-274" data-mp="1kpbe2c4hck6l">
                        {registration.fullName}
                      </button>
                      <p className="mt-0.5 text-ink-muted" data-id="element-275" data-mp="11z3e22w2u9um">{registration.email}</p>
                      <p className="mt-1 text-ink-muted" data-id="element-276" data-mp="11x0il3c5noii">
                        {registration.jobTitle}, {registration.organisation}
                      </p>
                      <p className="mt-1 font-mono text-xs text-ink-muted/80" data-id="element-277" data-mp="1bqpmxskxzwu7">{registration.ticketCode}</p>
                    </td>
                    <td className="hidden px-4 py-4 text-ink-muted lg:table-cell" data-id="element-278" data-mp="e7xzrs5sbpmf">{event ? event.title : 'Unknown'}</td>
                    <td className="hidden px-4 py-4 text-ink-muted sm:table-cell" data-id="element-279" data-mp="2wiv9bjxkalg5">
                      {format(new Date(registration.submittedAt), 'd MMM, HH:mm')}
                    </td>
                    <td className="px-4 py-4" data-id="element-280" data-mp="2nigiy65qqg5s">
                      <span className={['inline-block rounded-full border px-3 py-1 text-xs capitalize', statusStyles[registration.status]].join(' ')} data-id="element-281" data-mp="2nh5fy8k4dcfq">
                        {registration.status}
                      </span>
                    </td>
                    <td className="px-4 py-4" data-id="element-282" data-mp="24r5xi4isavla">
                      <div className="flex flex-wrap justify-end gap-2" data-id="element-283" data-mp="24pusy2hiljt4">
                        {registration.status !== 'approved' && <button type="button" onClick={() => decide(registration.id, 'approved')} className="inline-flex items-center gap-1.5 rounded-full bg-signal px-3.5 py-1.5 text-xs font-medium text-white transition-colors hover:bg-signal-deep" data-id="element-284" data-mp="1uuazrnuc4z82">
                            <CheckIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-285" data-mp="3uy3txd61rwy2" />
                            Approve
                          </button>}
                        {registration.status === 'approved' && <button type="button" onClick={() => resendTicket(registration.id)} className="inline-flex items-center gap-1.5 rounded-full border border-paper-line px-3.5 py-1.5 text-xs font-medium text-ink transition-colors hover:border-signal/40" data-id="element-286" data-mp="3766pi74xg76z">
                            <SendIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-287" data-mp="vh5g40fjxdfk" />
                            Resend ticket
                          </button>}
                        {registration.status !== 'declined' && <button type="button" onClick={() => decide(registration.id, 'declined')} className="inline-flex items-center gap-1.5 rounded-full border border-ink/25 px-3.5 py-1.5 text-xs font-medium text-ink-soft transition-colors hover:bg-ink/5" data-id="element-288" data-mp="2kyki7bdrwfks">
                            <XIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-289" data-mp="33nr5lbpihvcx" />
                            Decline
                          </button>}
                      </div>
                    </td>
                  </tr>;
            })}
            </tbody>
          </table>

          {filtered.length === 0 && <div className="px-6 py-16 text-center" data-id="element-290" data-mp="38lspfyurhypg">
              <TicketIcon className="mx-auto h-6 w-6 text-ink-muted" aria-hidden="true" data-id="element-291" data-mp="1j9gri0sq08zq" />
              <p className="mt-3 font-display text-xl font-semibold text-ink" data-id="element-292" data-mp="1eambf6m50yjb">Nothing in this queue</p>
              <p className="mx-auto mt-2 max-w-sm text-sm text-ink-muted" data-id="element-293" data-mp="3ecvkms190x17">
                Registrations appear here the moment someone registers on an event page.
              </p>
            </div>}
        </div>

        <button type="button" onClick={reset} className="mt-6 inline-flex items-center gap-2 text-xs text-ink-muted transition-colors hover:text-ink" data-id="element-294" data-mp="3nb78wnen7iqh">
          <RotateCcwIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-295" data-mp="14sih9cu57no" />
          Reset test data
        </button>

        {showOutbox && <section className="mt-16" data-id="element-296" data-mp="av3msx0fliv3">
            <h2 className="font-display text-2xl font-semibold text-ink" data-id="element-297" data-mp="aw6cj1m0pf9f">Outbox</h2>
            <p className="mt-2 max-w-xl text-sm leading-relaxed text-ink-muted" data-id="element-298" data-mp="ex8hlz2m8ljb">
              Every email this desk has sent: receipt confirmations, approved tickets with the PDF attached, and decline
              notes.
            </p>
            {outbox.length === 0 ? <p className="mt-8 rounded-xl border border-dashed border-paper-line bg-white px-6 py-12 text-center text-sm text-ink-muted" data-id="element-299" data-mp="yytigoy7fs2c">
                No emails yet. Register on an event page or approve a registration to see them here.
              </p> : <ul className="mt-8 space-y-8" data-id="element-300" data-mp="p2q66q9n2d1o">
                {outbox.slice(0, 6).map(email => {
            const event = getEventById(email.eventId);
            const registration = registrations.find(item => item.id === email.registrationId);
            if (!event || !registration) return null;
            return <li key={email.id} data-id="element-301" data-mp="17yq38fhh46s4">
                      <EmailPreview kind={email.kind} event={event} registration={registration} sentAt={email.sentAt} data-id="element-302" data-mp="17xewq7wn8kxb" />
                    </li>;
          })}
              </ul>}
          </section>}
      </div>

      {inspecting && <div role="dialog" aria-modal="true" aria-label={`Registration details for ${inspecting.fullName}`} className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-ink/70 p-5 py-12" onClick={() => setInspecting(null)} data-id="element-303" data-mp="1voam6ydmn41e">
          <div className="w-full max-w-2xl space-y-6 rounded-xl bg-paper p-6 sm:p-8" onClick={clickEvent => clickEvent.stopPropagation()} data-id="element-304" data-mp="25ghvcfxt65p4">
            <div className="flex items-start justify-between gap-6" data-id="element-305" data-mp="2y4nhn74ig2r2">
              <div data-id="element-306" data-mp="332f8ryxbzku7">
                <p className="eyebrow text-signal" data-id="element-307" data-mp="332yr3gqw8b8r">Registration detail</p>
                <h2 className="mt-2 font-display text-2xl font-semibold text-ink" data-id="element-308" data-mp="2o5g1ydaazw0s">{inspecting.fullName}</h2>
                <p className="mt-1 text-sm text-ink-muted" data-id="element-309" data-mp="2t498tmv34orz">
                  {inspecting.jobTitle}, {inspecting.organisation} · {inspecting.profile}
                </p>
                <p className="mt-1 text-sm text-ink-muted" data-id="element-310" data-mp="1csdldi87f6gm">{inspecting.email}</p>
              </div>
              <button type="button" onClick={() => setInspecting(null)} className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-paper-line text-ink" aria-label="Close details" data-id="element-311" data-mp="1hn9jn041aq26">
                <XIcon className="h-4 w-4" aria-hidden="true" data-id="element-312" data-mp="8rmd237anydn" />
              </button>
            </div>

            <div className="rounded-xl border border-paper-line bg-white p-5" data-id="element-313" data-mp="4opq60aq1g3w">
              <h3 className="eyebrow text-ink-muted" data-id="element-314" data-mp="3d75dgck4d324">Why they want to attend</h3>
              <p className="mt-2 text-sm leading-relaxed text-ink-soft" data-id="element-315" data-mp="388lpy7pvnj7n">{inspecting.intent}</p>
              {inspecting.dietary && <p className="mt-4 text-sm text-ink-muted" data-id="element-316" data-mp="3m11y1mxmy7ff">
                  <span className="text-ink" data-id="element-317" data-mp="3h762ss8rayny">Dietary or access:</span> {inspecting.dietary}
                </p>}
            </div>

            {getEventById(inspecting.eventId) && <ETicket event={getEventById(inspecting.eventId)!} registration={inspecting} state={inspecting.status === 'approved' ? 'issued' : 'pending'} data-id="element-318" data-mp="2pfzzy41nimuz" />}

            <div className="flex flex-wrap gap-3" data-id="element-319" data-mp="1fj4oq2uk0ffs">
              <button type="button" onClick={() => {
            decide(inspecting.id, 'approved');
            setInspecting(null);
          }} className="inline-flex items-center gap-2 rounded-full bg-signal px-5 py-2.5 text-sm font-medium text-white transition-colors hover:bg-signal-deep" data-id="element-320" data-mp="1t983k8r3t6v7">
                <CheckIcon className="h-4 w-4" aria-hidden="true" data-id="element-321" data-mp="2c7kfg29eguuh" />
                Approve and email ticket
              </button>
              <button type="button" onClick={() => {
            decide(inspecting.id, 'declined');
            setInspecting(null);
          }} className="inline-flex items-center gap-2 rounded-full border border-ink/25 px-5 py-2.5 text-sm font-medium text-ink-soft transition-colors hover:bg-ink/5" data-id="element-322" data-mp="2787w3rhqm5wn">
                <XIcon className="h-4 w-4" aria-hidden="true" data-id="element-323" data-mp="2them9flufs0v" />
                Decline
              </button>
            </div>
          </div>
        </div>}
    </main>;
}